import { Directive, input, output } from '@angular/core';

/** How far a sheet has to be pulled down, or how fast, before letting go closes it. */
const CLOSE_DISTANCE = 100;
const CLOSE_SPEED = 0.6; // pixels per millisecond

/**
 * Pull a bottom sheet down to close it. Put it on the part that is held — the grip and the header — and give
 * it the sheet itself: the sheet follows the finger downwards, and on letting go it either closes
 * (`dismissed`) or springs back. Only a finger or pen; with a mouse the close button does the job.
 *
 * The handle gets `touch-action: none`, so the browser does not scroll the page while the sheet is pulled.
 */
@Directive({
  selector: '[appSheetDrag]',
  standalone: true,
  host: {
    style: 'touch-action: none',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'onPointerUp($event)',
    '(pointercancel)': 'springBack()',
  },
})
export class SheetDragDirective {
  readonly appSheetDrag = input.required<HTMLElement>();
  readonly dismissed = output<void>();

  private start: { y: number; time: number; pointerId: number } | null = null;
  private distance = 0;

  protected onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' || (event.target as Element | null)?.closest('button, a')) {
      return;
    }

    this.start = { y: event.clientY, time: event.timeStamp, pointerId: event.pointerId };
    this.distance = 0;
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    this.appSheetDrag().style.transition = 'none';
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.start || event.pointerId !== this.start.pointerId) {
      return;
    }

    // Only downwards: pulling a sheet up has nowhere to go.
    this.distance = Math.max(0, event.clientY - this.start.y);
    this.appSheetDrag().style.transform = `translateY(${this.distance}px)`;
  }

  protected onPointerUp(event: PointerEvent): void {
    if (!this.start) {
      return;
    }

    const speed = this.distance / Math.max(1, event.timeStamp - this.start.time);
    const closes = this.distance > CLOSE_DISTANCE || (this.distance > 20 && speed > CLOSE_SPEED);
    this.start = null;

    if (closes) {
      this.dismissed.emit();
      // The host may keep the sheet open (unsaved input): it must not stay stuck halfway down.
      this.springBack();

      return;
    }

    this.springBack();
  }

  protected springBack(): void {
    this.start = null;
    const sheet = this.appSheetDrag();
    sheet.style.transition = 'transform 160ms ease-out';
    sheet.style.transform = '';
  }
}
